class Analytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.events = [];
    this.deviceInfo = this.getDeviceInfo();
    // Use the configured API URL; an explicitly-empty value means same-origin
    // (the container serves API + build together). Only fall back to localhost
    // when the var is undefined (e.g. dev without an env file).
    this.apiUrl = process.env.REACT_APP_API_URL !== undefined
      ? process.env.REACT_APP_API_URL
      : 'http://localhost:3003';
    this.batchSize = 20;
    this.flushInterval = 30000; // 30 seconds
    this.retryAttempts = 3;
    
    // ANALYTICS DISABLED FOR OFFLINE TESTING
    console.log('📊 Analytics: Disabled for offline functionality testing');
    this.fallbackMode = true;
    // setTimeout(() => {
    //   this.initAnalytics();
    // }, 3000);
  }

  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  getDeviceInfo() {
    const ua = navigator.userAgent;
    const screen = window.screen;
    
    return {
      userAgent: ua,
      platform: navigator.platform,
      screenWidth: screen.width,
      screenHeight: screen.height,
      devicePixelRatio: window.devicePixelRatio,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      deviceType: this.getDeviceType(ua),
      browser: this.getBrowser(ua),
      os: this.getOS(ua),
      timestamp: Date.now()
    };
  }

  getDeviceType(ua) {
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  getBrowser(ua) {
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Other';
  }

  getOS(ua) {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Other';
  }

  async initAnalytics() {
    // Skip analytics initialization if offline
    if (!navigator.onLine) {
      console.log('📊 Analytics: Skipping initialization - currently offline');
      this.fallbackMode = true;
      return;
    }
    
    // Initialize session with API
    try {
      await this.createSession();
      console.log('📊 Analytics: Using MySQL database backend');
    } catch (error) {
      console.warn('Failed to create analytics session:', error);
      
      // Check if this is an ad blocker issue
      const isAdBlocked = error.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
                          error.message?.includes('Failed to fetch');
      
      if (isAdBlocked) {
        console.log('📊 Analytics: Ad blocker detected, using localStorage fallback');
      } else {
        console.log('📊 Analytics: API unavailable, using localStorage fallback');
      }
      
      // Fallback to localStorage if API fails
      this.fallbackMode = true;
    }

    // Track app start
    this.trackEvent('app_start', {
      sessionId: this.sessionId,
      deviceInfo: this.deviceInfo,
      timestamp: Date.now(),
      fallbackMode: this.fallbackMode
    });

    // Track service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'UPDATE_AVAILABLE') {
          this.trackEvent('app_update_available', {
            timestamp: Date.now()
          });
        }
      });
    }

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.trackEvent('visibility_change', {
        hidden: document.hidden,
        timestamp: Date.now()
      });
      
      // Flush events when page becomes hidden
      if (document.hidden) {
        this.flushEvents();
      }
    });

    // Track online/offline status
    window.addEventListener('online', () => {
      this.trackEvent('network_status', { online: true, timestamp: Date.now() });
      // Retry failed events when back online
      this.retryFailedEvents();
    });
    
    window.addEventListener('offline', () => {
      this.trackEvent('network_status', { online: false, timestamp: Date.now() });
    });

    // Periodically flush events to API
    this.flushIntervalId = setInterval(() => this.flushEvents(), this.flushInterval);
    
    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flushEvents(true));
    
    // Flush when leaving the page
    window.addEventListener('pagehide', () => this.flushEvents(true));
  }

  async createSession() {
    const sessionData = {
      sessionId: this.sessionId,
      userAgent: this.deviceInfo.userAgent,
      platform: this.deviceInfo.platform,
      screenWidth: this.deviceInfo.screenWidth,
      screenHeight: this.deviceInfo.screenHeight,
      devicePixelRatio: this.deviceInfo.devicePixelRatio,
      language: this.deviceInfo.language,
      cookieEnabled: this.deviceInfo.cookieEnabled,
      deviceType: this.deviceInfo.deviceType,
      browser: this.deviceInfo.browser,
      os: this.deviceInfo.os,
      startTime: this.startTime,
      isPWA: window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
    };

    const response = await fetch(`${this.apiUrl}/api/analytics/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(sessionData)
    });

    if (!response.ok) {
      throw new Error(`Session creation failed: ${response.status}`);
    }

    return response.json();
  }

  trackEvent(eventType, data = {}) {
    const event = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
      type: eventType,
      sessionId: this.sessionId,
      timestamp: Date.now(),
      data: data
    };

    this.events.push(event);
    console.log(`📊 Analytics: ${eventType}`, data);

    // Auto-flush if batch size reached
    if (this.events.length >= this.batchSize) {
      this.flushEvents();
    }

    // Always save to localStorage for immediate admin dashboard access
    this.saveToStorage();
  }

  async flushEvents(isUnloading = false) {
    if (this.events.length === 0) return;

    const eventsToSend = [...this.events];
    this.events = [];

    try {
      if (this.fallbackMode) {
        // Save to localStorage if API is not available
        this.saveToStorage();
        return;
      }

      const response = await fetch(`${this.apiUrl}/api/analytics/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ events: eventsToSend }),
        keepalive: isUnloading // Use keepalive for unload events
      });

      if (!response.ok) {
        throw new Error(`Batch send failed: ${response.status}`);
      }

      const result = await response.json();
      console.log(`📊 Analytics: Sent ${result.insertedCount}/${result.eventsReceived} events`);
      
      // Reset failure count on success
      this.consecutiveFailures = 0;

    } catch (error) {
      console.warn('Failed to send analytics events:', error);
      
      // Increment failure count
      this.consecutiveFailures = (this.consecutiveFailures || 0) + 1;
      
      // Put events back in queue for retry
      this.events.unshift(...eventsToSend);
      
      // Store failed events for retry
      this.storeFailedEvents(eventsToSend);
      
      // Check if this is an ad blocker or network issue
      const isAdBlocked = error.message.includes('ERR_BLOCKED_BY_CLIENT') || 
                          error.message.includes('Failed to fetch');
      
      // Switch to fallback mode if consistently failing or ad blocked
      if (isAdBlocked || !navigator.onLine || this.consecutiveFailures >= this.retryAttempts) {
        console.log('📊 Analytics: Switching to localStorage fallback mode');
        this.fallbackMode = true;
        this.saveToStorage();
      }
    }
  }

  storeFailedEvents(events) {
    try {
      const existing = JSON.parse(localStorage.getItem('analytics_failed_events') || '[]');
      existing.push(...events);
      
      // Keep only last 500 failed events
      if (existing.length > 500) {
        existing.splice(0, existing.length - 500);
      }
      
      localStorage.setItem('analytics_failed_events', JSON.stringify(existing));
    } catch (error) {
      console.warn('Failed to store failed events:', error);
    }
  }

  async retryFailedEvents() {
    try {
      const failedEvents = JSON.parse(localStorage.getItem('analytics_failed_events') || '[]');
      if (failedEvents.length === 0) return;

      const response = await fetch(`${this.apiUrl}/api/analytics/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ events: failedEvents })
      });

      if (response.ok) {
        localStorage.removeItem('analytics_failed_events');
        console.log(`📊 Analytics: Retried ${failedEvents.length} failed events`);
        this.fallbackMode = false;
      }
    } catch (error) {
      console.warn('Failed to retry events:', error);
    }
  }

  // Recipe tracking methods
  trackRecipeView(recipeId, recipeName) {
    this.trackEvent('recipe_view', {
      recipeId,
      recipeName,
      timestamp: Date.now()
    });
  }

  trackCategoryView(categoryId, categoryName) {
    this.trackEvent('category_view', {
      categoryId,
      categoryName,
      timestamp: Date.now()
    });
  }

  trackAudioPlay(recipeId, stepNumber, audioFile, playbackRate = 1) {
    this.trackEvent('audio_play', {
      recipeId,
      stepNumber,
      audioFile,
      playbackRate,
      timestamp: Date.now()
    });
  }

  trackAudioPause(recipeId, stepNumber, audioFile, currentTime) {
    this.trackEvent('audio_pause', {
      recipeId,
      stepNumber,
      audioFile,
      currentTime,
      timestamp: Date.now()
    });
  }

  trackAudioComplete(recipeId, stepNumber, audioFile, duration) {
    this.trackEvent('audio_complete', {
      recipeId,
      stepNumber,
      audioFile,
      duration,
      timestamp: Date.now()
    });
  }

  trackAudioSpeedChange(recipeId, stepNumber, oldRate, newRate) {
    this.trackEvent('audio_speed_change', {
      recipeId,
      stepNumber,
      oldRate,
      newRate,
      timestamp: Date.now()
    });
  }

  trackStepNavigation(recipeId, fromStep, toStep) {
    this.trackEvent('step_navigation', {
      recipeId,
      fromStep,
      toStep,
      timestamp: Date.now()
    });
  }

  trackIngredientHighlight(recipeId, stepNumber, ingredientIndex) {
    this.trackEvent('ingredient_highlight', {
      recipeId,
      stepNumber,
      ingredientIndex,
      timestamp: Date.now()
    });
  }

  trackAppInstall() {
    this.trackEvent('app_install', {
      timestamp: Date.now()
    });
  }

  trackWakeLockRequest(recipeId) {
    this.trackEvent('wake_lock_request', {
      recipeId,
      timestamp: Date.now()
    });
  }

  async trackError(error, context = {}) {
    const errorData = {
      sessionId: this.sessionId,
      errorMessage: error.message || error.toString(),
      errorStack: error.stack || '',
      context: context,
      timestamp: Date.now()
    };

    // Track as event
    this.trackEvent('error', errorData);

    // Also send directly to error endpoint for immediate logging
    try {
      if (!this.fallbackMode) {
        await fetch(`${this.apiUrl}/api/analytics/error`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(errorData)
        });
      }
    } catch (err) {
      console.warn('Failed to send error directly:', err);
    }
  }

  // Debug method to check API connectivity
  async testApiConnection() {
    try {
      const response = await fetch(`${this.apiUrl}/api/analytics/overview`);
      if (response.ok) {
        console.log('📊 Analytics API: Connected ✅');
        return { connected: true, source: 'api' };
      } else {
        console.log('📊 Analytics API: Server error ❌');
        return { connected: false, source: 'localStorage', reason: 'server_error' };
      }
    } catch (error) {
      const isAdBlocked = error.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
                          error.message?.includes('Failed to fetch');
      
      if (isAdBlocked) {
        console.log('📊 Analytics API: Blocked by ad blocker 🚫');
        return { connected: false, source: 'localStorage', reason: 'ad_blocked' };
      } else {
        console.log('📊 Analytics API: Network error 📡');
        return { connected: false, source: 'localStorage', reason: 'network_error' };
      }
    }
  }

  // Storage methods
  saveToStorage() {
    try {
      const data = {
        sessionId: this.sessionId,
        startTime: this.startTime,
        deviceInfo: this.deviceInfo,
        events: this.events,
        lastSaved: Date.now()
      };
      
      localStorage.setItem('analytics_session', JSON.stringify(data));
      
      // Also save to persistent analytics storage
      const persistentData = JSON.parse(localStorage.getItem('analytics_persistent') || '[]');
      
      // Add this session's events to persistent storage
      persistentData.push(...this.events.filter(event => 
        !persistentData.some(existing => existing.id === event.id)
      ));
      
      // Keep only last 10,000 events
      if (persistentData.length > 10000) {
        persistentData.splice(0, persistentData.length - 10000);
      }
      
      localStorage.setItem('analytics_persistent', JSON.stringify(persistentData));
      
    } catch (error) {
      console.warn('Failed to save analytics:', error);
    }
  }

  // Admin methods - API-based
  async getAllData() {
    try {
      if (this.fallbackMode) {
        // Fallback to localStorage
        const sessionData = JSON.parse(localStorage.getItem('analytics_session') || '{}');
        const persistentData = JSON.parse(localStorage.getItem('analytics_persistent') || '[]');
        
        return {
          currentSession: sessionData,
          allEvents: persistentData,
          deviceInfo: this.deviceInfo,
          sessionId: this.sessionId,
          source: 'localStorage'
        };
      }

      // Fetch from API
      const [overview, recipes, audio, categories, daily, errors] = await Promise.all([
        fetch(`${this.apiUrl}/api/analytics/overview`).then(r => r.json()),
        fetch(`${this.apiUrl}/api/analytics/recipes`).then(r => r.json()),
        fetch(`${this.apiUrl}/api/analytics/audio`).then(r => r.json()),
        fetch(`${this.apiUrl}/api/analytics/categories`).then(r => r.json()),
        fetch(`${this.apiUrl}/api/analytics/daily`).then(r => r.json()),
        fetch(`${this.apiUrl}/api/analytics/errors`).then(r => r.json())
      ]);

      return {
        overview: overview.data,
        recipes: recipes.data,
        audio: audio.data,
        categories: categories.data,
        daily: daily.data,
        errors: errors.data,
        currentSession: {
          sessionId: this.sessionId,
          deviceInfo: this.deviceInfo,
          startTime: this.startTime
        },
        source: 'api'
      };
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      
      // Fallback to localStorage
      return this.getLocalStorageData();
    }
  }

  getLocalStorageData() {
    const allEvents = JSON.parse(localStorage.getItem('analytics_persistent') || '[]');
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    const oneWeek = 7 * oneDay;
    const oneMonth = 30 * oneDay;

    const stats = {
      totalEvents: allEvents.length,
      uniqueSessions: new Set(allEvents.map(e => e.sessionId)).size,
      dateRange: {
        oldest: allEvents.length > 0 ? Math.min(...allEvents.map(e => e.timestamp)) : now,
        newest: allEvents.length > 0 ? Math.max(...allEvents.map(e => e.timestamp)) : now
      },
      eventTypes: {},
      timeframes: {
        last24h: allEvents.filter(e => now - e.timestamp < oneDay).length,
        last7d: allEvents.filter(e => now - e.timestamp < oneWeek).length,
        last30d: allEvents.filter(e => now - e.timestamp < oneMonth).length
      },
      recipes: {},
      audio: {},
      devices: {},
      browsers: {},
      errors: allEvents.filter(e => e.type === 'error')
    };

    // Count event types and build stats
    allEvents.forEach(event => {
      stats.eventTypes[event.type] = (stats.eventTypes[event.type] || 0) + 1;
      
      if (event.type === 'recipe_view' && event.data.recipeId) {
        const recipeId = event.data.recipeId;
        if (!stats.recipes[recipeId]) {
          stats.recipes[recipeId] = {
            id: recipeId,
            name: event.data.recipeName,
            views: 0,
            audioPlays: 0
          };
        }
        stats.recipes[recipeId].views++;
      }
      
      if (event.type === 'audio_play') {
        const recipeId = event.data.recipeId;
        if (stats.recipes[recipeId]) {
          stats.recipes[recipeId].audioPlays++;
        }
        
        const speed = event.data.playbackRate || 1;
        stats.audio[speed] = (stats.audio[speed] || 0) + 1;
      }
      
      if (event.type === 'app_start' && event.data.deviceInfo) {
        const device = event.data.deviceInfo;
        const deviceKey = `${device.deviceType}-${device.browser}-${device.os}`;
        stats.devices[deviceKey] = (stats.devices[deviceKey] || 0) + 1;
        stats.browsers[device.browser] = (stats.browsers[device.browser] || 0) + 1;
      }
    });

    return {
      overview: stats,
      currentSession: {
        sessionId: this.sessionId,
        deviceInfo: this.deviceInfo,
        startTime: this.startTime
      },
      source: 'localStorage'
    };
  }

  async getStats() {
    try {
      const response = await fetch(`${this.apiUrl}/api/analytics/overview`);
      if (!response.ok) throw new Error('API unavailable');
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.warn('Failed to fetch stats from API, using localStorage:', error);
      return this.getLocalStorageData().overview;
    }
  }

  async clearData() {
    try {
      if (!this.fallbackMode) {
        const response = await fetch(`${this.apiUrl}/api/analytics/clear`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ confirm: 'DELETE_ALL_ANALYTICS' })
        });
        
        if (response.ok) {
          console.log('📊 Analytics: Server data cleared');
        }
      }
    } catch (error) {
      console.warn('Failed to clear server data:', error);
    }
    
    // Clear local data
    localStorage.removeItem('analytics_session');
    localStorage.removeItem('analytics_persistent');
    localStorage.removeItem('analytics_failed_events');
    this.events = [];
    
    console.log('📊 Analytics: Local data cleared');
  }

  // Cleanup method
  destroy() {
    if (this.flushIntervalId) {
      clearInterval(this.flushIntervalId);
    }
    this.flushEvents(true);
  }
}

// Create global analytics instance
const analytics = new Analytics();

export default analytics;